import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CommonButton from "../../components/button/CommonButton";
import InlineLoader from "../../components/loader/InlineLoader";
import { appApiHandler } from "../../api/appApiHandler";
import "./SupportTicket.css";
import { FaUser } from "react-icons/fa";
import EmptyState from "../../components/EmptyState";
import HomeHeader from "../../components/header/HomeHeader";
import CommonFooter from "../../components/footer/CommonFooter";
import SupportTicketCard from "./component/supportTicketCard";
import Breadcrumb from "../../components/breadcrumb";
import { string } from "../../constants/string";
import { showErrorToast } from "../../utils/toast";
import { setNewMessage } from "../../store/slices/supportTicketState";

const SupportTicket = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.userState);
  const { isLoggedIn } = useSelector((state) => state.authState);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);

  const onPressCreateTicket = () => {
    if (!userData) {
      showErrorToast(string.error.please_login_to_your_account);
      return;
    }
    navigate("/create-support-ticket");
  };

  const getTickets = async () => {
    const reqData = {
      user_id: userData?._id,
    };
    const response = await appApiHandler.getSupportTickets(reqData);
    setLoading(false);
    if (response?.status === 200 && response?.data) {
      setTickets(response?.data);
      const hasUnread = Array.isArray(response?.data) && response.data.some((t) => Number(t?.unread_count) > 0);
      dispatch(setNewMessage(hasUnread ? 1 : 0));
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      setLoading(true);
      getTickets();
    }
  }, [isLoggedIn]);

  const handleTicketClick = useCallback(
    async (ticketId) => {
      const data = {
        support_ticket_id: ticketId,
        user_read: 1,
      };
      const response = await appApiHandler.updateSupportTicket(data);
      dispatch(setNewMessage(0))
      navigate("/support-chat", {
        state: {
          ticket_id: ticketId,
        },
      });
    },
    [navigate]
  );

  const renderTicketCard = useCallback(
    (item) => {
      return (
        <SupportTicketCard
          key={item?._id}
          ticket={item}
          onClick={handleTicketClick}
        />
      );
    },
    [handleTicketClick]
  );

  return (
    <div className="support-ticket-container">
      <HomeHeader />
      <Breadcrumb
        items={[
          {
            label: "Home",
            path: "/",
          },
          {
            label: "Support Tickets",
            isActive: true,
          },
        ]}
      />
      <div className="support-ticket-content">
        {loading ? (
          <InlineLoader text="Loading tickets..." />
        ) : isLoggedIn ? (
          <>
            {tickets.length > 0 && (
              <div className="support-ticket-header">
                <CommonButton
                  title="Create Ticket"
                  onPress={onPressCreateTicket}
                  customClassName="create-ticket-btn"
                />
              </div>
            )}
            <div className="tickets-list">
              {tickets.length > 0 ? (
                tickets.map(renderTicketCard)
              ) : (
                <EmptyState
                  title="No tickets here"
                  description="We're always ready to help — just open a support request!"
                  buttonText="Create Ticket"
                  onButtonClick={onPressCreateTicket}
                />
              )}
            </div>
          </>
        ) : (
          <EmptyState
            icon={FaUser}
            title="Sign In to Continue"
            description="please login with your account. Enjoy a seamless shopping experience with personalized recommendations and more!"
            buttonText="Login"
            onButtonClick={() => navigate("/login")}
            iconSize={48}
          />
        )}
      </div>
      <CommonFooter />
    </div>
  );
};

export default SupportTicket;
